import { Calendar } from 'lucide-react';
import type { ComponentPropsWithRef } from 'react';
import { cn } from '@/shared/lib/cn';
import { useFormFieldContext } from './form-field-context';

export interface DateInputProps extends Omit<ComponentPropsWithRef<'input'>, 'type' | 'size'> {
  /** `date` — sam dzien, `datetime-local` — dzien i godzina. */
  withTime?: boolean;
  inputSize?: 'sm' | 'md' | 'lg';
  invalid?: boolean;
}

const sizeClasses = {
  sm: 'h-9 pl-9 pr-3 text-[0.8125rem]',
  md: 'h-11 pl-10 pr-3.5 text-sm',
  lg: 'h-13 pl-11 pr-4 text-sm',
} as const;

/**
 * Pole daty z ikona kalendarza — jak „Data rozpoczęcia / Data zakończenia”
 * na ekranie tworzenia turnieju.
 *
 * Opiera sie na natywnym `input[type=date]`: dostajemy za darmo lokalny format
 * zapisu, obsluge klawiatury i kalendarz systemowy na urzadzeniach mobilnych.
 * Natywna ikonke przegladarki ukrywamy i rysujemy wlasna, spojna z reszta.
 */
export function DateInput({
  className,
  withTime = false,
  inputSize = 'md',
  invalid,
  id,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  required,
  ...props
}: DateInputProps) {
  const field = useFormFieldContext();
  const resolvedInvalid = invalid ?? ariaInvalid ?? field?.invalid ?? false;

  return (
    <div className="relative flex items-center">
      <Calendar
        aria-hidden="true"
        className="text-fg-subtle pointer-events-none absolute left-3.5 size-4"
      />

      <input
        type={withTime ? 'datetime-local' : 'date'}
        id={id ?? field?.controlId}
        aria-describedby={ariaDescribedBy ?? field?.describedBy}
        aria-invalid={resolvedInvalid || undefined}
        required={required ?? field?.required}
        className={cn(
          'bg-surface-raised text-fg border-border-input w-full min-w-0 rounded-lg border',
          'transition-[border-color,box-shadow] duration-150',
          'focus-visible:border-brand focus-visible:ring-brand/35 focus-visible:ring-2 focus-visible:outline-none',
          'disabled:bg-surface-sunken disabled:text-fg-disabled disabled:cursor-not-allowed',
          'aria-invalid:border-danger-500 aria-invalid:focus-visible:ring-danger-500/30',
          // Chowamy natywna ikone kalendarza — mamy wlasna po lewej stronie.
          '[&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0',
          '[&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:w-full',
          '[&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0',
          sizeClasses[inputSize],
          className,
        )}
        {...props}
      />
    </div>
  );
}
